import { Card, CardContent, CardHeader, CardTitle } from '../core/card';
import { Progress } from '../core/progress';
import { StarRatingDisplay } from './star-rating-display';
import { cn } from '../../utils';

export type FeedbackQuestionResult = {
  questionId: string;
  questionText: string;
  questionType: 'rating' | 'text';
  averageRating: number | null;
  totalResponses: number;
  distribution: number[]; // counts for [1, 2, 3, 4, 5] (rating only)
  textResponses?: string[]; // text answers (text only)
};

export type FeedbackResultsCardProps = {
  question: FeedbackQuestionResult;
  className?: string;
  labels?: {
    responses?: string;
    distribution?: string;
    noResponses?: string;
  };
};

export function FeedbackResultsCard({
  question,
  className,
  labels,
}: FeedbackResultsCardProps) {
  const isRating = question.questionType === 'rating';

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium leading-snug">
          {question.questionText}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isRating ? (
          <RatingResults question={question} labels={labels} />
        ) : (
          <TextResults question={question} labels={labels} />
        )}
      </CardContent>
    </Card>
  );
}

function RatingResults({
  question,
  labels,
}: {
  question: FeedbackQuestionResult;
  labels?: FeedbackResultsCardProps['labels'];
}) {
  const maxCount = Math.max(...question.distribution, 1);

  return (
    <>
      <div className="flex items-center justify-between">
        <StarRatingDisplay value={question.averageRating ?? 0} />
        <span className="text-xs text-muted-foreground">
          {question.totalResponses} {labels?.responses ?? 'responses'}
        </span>
      </div>

      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((star) => {
          const starCount = question.distribution[star - 1] ?? 0;
          const percent = question.totalResponses > 0 ? (starCount / maxCount) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-2 text-xs">
              <span className="w-3 text-right text-muted-foreground">{star}</span>
              <Progress value={percent} className="h-2 flex-1" />
              <span className="w-6 text-right text-muted-foreground">{starCount}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

function TextResults({
  question,
  labels,
}: {
  question: FeedbackQuestionResult;
  labels?: FeedbackResultsCardProps['labels'];
}) {
  const responses = question.textResponses ?? [];

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {question.totalResponses} {labels?.responses ?? 'responses'}
        </span>
      </div>

      {responses.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {labels?.noResponses ?? 'No responses yet'}
        </p>
      ) : (
        <div className="max-h-60 space-y-2 overflow-y-auto">
          {responses.map((text, i) => (
            <div
              key={i}
              className="rounded-md border bg-muted/50 px-3 py-2 text-sm"
            >
              {text}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
